import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Input, Space, message } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';

interface NavLink {
  label: string;
  href: string;
  sort_order: number;
}

interface NavBarEditorProps {
  data: NavLink[];
  onSave: (data: NavLink[]) => void;
}

const NavBarEditor: React.FC<NavBarEditorProps> = ({ data, onSave }) => {
  const [links, setLinks] = useState<NavLink[]>(
    data && data.length > 0
      ? data
      : [
          { label: 'Features', href: '/#features', sort_order: 1 },
          { label: 'How It Works', href: '/#how-it-works', sort_order: 2 },
          { label: 'Resources', href: '/#resources', sort_order: 3 },
        ]
  );

  useEffect(() => {
    if (Array.isArray(data)) {
      setLinks([...data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      return;
    }
    setLinks([
      { label: 'Features', href: '/#features', sort_order: 1 },
      { label: 'How It Works', href: '/#how-it-works', sort_order: 2 },
      { label: 'Resources', href: '/#resources', sort_order: 3 },
    ]);
  }, [data]);

  const handleAdd = () => {
    const newLink: NavLink = {
      label: '新链接',
      href: '/#',
      sort_order: links.length + 1,
    };
    setLinks([...links, newLink]);
  };

  const handleRemove = (index: number) => {
    const newLinks = links.filter((_, i) => i !== index);
    // Re-sort
    newLinks.forEach((link, i) => {
      link.sort_order = i + 1;
    });
    setLinks(newLinks);
  };

  const handleChange = (index: number, field: keyof NavLink, value: string | number) => {
    const newLinks = [...links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setLinks(newLinks);
  };

  const handleSave = () => {
    // Validate
    for (const link of links) {
      if (!link.label || !link.href) {
        message.error('请填写所有链接的名称和地址');
        return;
      }
    }
    onSave(links);
  };

  return (
    <Card
      title="NavBar 导航链接配置"
      extra={
        <Space>
          <Button icon={<PlusOutlined />} onClick={handleAdd}>
            添加链接
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
            保存配置
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {links.map((link, index) => (
          <Card key={index} size="small" type="inner">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Form layout="inline">
                <Form.Item label="排序">
                  <Input
                    type="number"
                    value={link.sort_order}
                    onChange={(e) => handleChange(index, 'sort_order', parseInt(e.target.value) || 0)}
                    style={{ width: 80 }}
                  />
                </Form.Item>
                <Form.Item label="名称">
                  <Input
                    value={link.label}
                    onChange={(e) => handleChange(index, 'label', e.target.value)}
                    placeholder="Features"
                    style={{ width: 150 }}
                  />
                </Form.Item>
                <Form.Item label="链接">
                  <Input
                    value={link.href}
                    onChange={(e) => handleChange(index, 'href', e.target.value)}
                    placeholder="/#features"
                    style={{ width: 250 }}
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(index)}
                  >
                    删除
                  </Button>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        ))}
      </Space>
    </Card>
  );
};

export default NavBarEditor;
